import { useQuery } from '@tanstack/react-query'
import { authorizedFetch } from '@/lib/api'
import { useEmployerOrganisationOptional } from '@/context/employer-organisation-context'
import { useFocusStore } from '@/hooks/use-focus-store'

export interface ThreadFilePreview {
  id: string
  original_name: string
  mime_type: string
  created_at?: string | null
}

export interface ThreadObjectPreview {
  type: string
  id: string
  name?: string | null
  action: string
}

export interface Thread {
  id: string
  subject: string | null
  user_id: string | null
  created_at: string
  updated_at: string
  message_count: number
  summary: string | null
  activity?: Record<string, number> | null
  latest_object_update?: {
    object_type: string
    object_id: string
    object_name?: string
    action: string
    source_tool?: string
    text?: string
  } | null
  updates_digest?: string[] | null
  file_count?: number
  files?: ThreadFilePreview[]
  object_count?: number
  objects?: ThreadObjectPreview[]
  feedback_items: Array<{
    score: number
    tags: string[]
    comment: string
    at: string | null
    message_id: string
    run_id: string
  }> | null
}

export function useThreads(options?: { enabled?: boolean }) {
  const { focus } = useFocusStore()
  const employerOrg = useEmployerOrganisationOptional()
  const currentFocus = focus?.current_focus ?? null
  const activeOrganisationSlug = employerOrg?.activeOrganisationSlug ?? null
  const scopeEnabled = options?.enabled !== false && currentFocus !== null
  const { data: threads = [], isLoading, error } = useQuery<Thread[]>({
    queryKey: [
      'threads',
      currentFocus ?? 'none',
      currentFocus === 'employer' ? activeOrganisationSlug ?? 'none' : 'n/a',
    ],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (currentFocus) {
        params.set('focus', currentFocus)
      }
      if (currentFocus === 'employer' && activeOrganisationSlug) {
        params.set('organisation', activeOrganisationSlug)
      }
      const query = params.toString()
      const response = await authorizedFetch(
        query ? `/api/threads?${query}` : '/api/threads',
      )
      if (!response.ok) {
        throw new Error('Failed to fetch threads')
      }
      const data = await response.json()
      return Array.isArray(data) ? data : []
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false,
    enabled: scopeEnabled,
  })

  return {
    threads,
    isLoading,
    error,
  }
}

